import { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Lock,
  FileText,
  Trash2,
  Loader2,
  LogOut,
  ChevronDown,
  ChevronUp,
  Link as LinkIcon,
  PenTool,
  PenBox
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// ================= LOGIN =================

function LoginForm({ onLogin }) {

  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      onLogin(res.data.token);
    } catch {
      setError('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">

      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">

        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Password"
          className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3 text-white"
        />

        {error && <p className="text-red-400 text-sm">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 rounded-full bg-white text-black font-bold px-8 py-3"
        >
          {loading
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Lock className="w-4 h-4" />}
          Login
        </button>

      </form>

    </div>
  );
}


// ================= BRIEF CARD =================

function SubmissionCard({ item, token, onDelete }) {

  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Delete this submission?')) return;

    setDeleting(true);

    try {
      await axios.delete(
        `${API}/admin/briefs/${item.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onDelete(item.id);
    } catch {
      alert('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl">

      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex justify-between p-5 text-left"
      >
        <div>
          <p className="font-semibold">{item.name}</p>
          <p className="text-xs text-white/30">{item.email}</p>
        </div>

        {expanded ? <ChevronUp /> : <ChevronDown />}
      </button>


      {expanded && (
        <div className="px-5 pb-5 border-t border-white/[0.06] space-y-2">

          {Object.entries(item).map(([k, v]) => {
            if (['id', 'created_at'].includes(k)) return null;
            if (!v) return null;

            return (
              <div key={k}>
                <span className="text-[10px] text-white/30 uppercase">{k}</span>
                <p className="text-sm text-white/70">{v}</p>
              </div>
            );
          })}

          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex items-center gap-2 text-red-400 text-xs pt-3"
          >
            {deleting
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Trash2 className="w-3 h-3" />}
            Delete
          </button>

        </div>
      )}

    </div>
  );
}


// ================= INVITES =================

function InviteManager({ token }) {

  const [invites, setInvites] = useState([]);
  const [creating, setCreating] = useState(false);


  useEffect(() => {

    const fetchInvites = async () => {
      const res = await axios.get(
        `${API}/admin/wall/invites`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setInvites(res.data);
    };

    fetchInvites();

  }, [token]);


  const createInvite = async () => {
    setCreating(true);

    const res = await axios.post(
      `${API}/admin/wall/invites`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setInvites(prev => [res.data, ...prev]);
    setCreating(false);
  };


  return (
    <div className="space-y-4">

      <button
        onClick={createInvite}
        disabled={creating}
        className="bg-white text-black px-6 py-2 rounded-full text-sm"
      >
        Invite Oluştur
      </button>


      {invites.filter(i => !i.usedAt).map(i => {

        const link = `${window.location.origin}/wall/invite/${i.token}`;

        return (
          <div
            key={i.token}
            className="bg-white/[0.02] border border-white/[0.06] p-4 rounded-xl"
          >

            <p className="text-xs text-white/40 mb-2">
              {i.usedAt ? "Kullanıldı" : "Kullanılmadı"}
            </p>

            <input
              value={link}
              readOnly
              className="w-full bg-black border border-white/[0.1] px-3 py-2 text-xs"
              onClick={(e) => e.target.select()}
            />

          </div>
        );
      })}

    </div>
  );
}


// ================= SIGNATURES =================

function SignatureManager({ token }) {

  const [items, setItems] = useState([]);

  useEffect(() => {

    const fetchItems = async () => {
      const res = await axios.get(
        `${API}/admin/wall/signatures?status=pending`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setItems(res.data);
    };

    fetchItems();

  }, [token]);

  const approve = async (id) => {
    await axios.post(
      `${API}/admin/wall/signatures/${id}/approve`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setItems(prev => prev.filter(x => x.id !== id));
  };


  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;

    await axios.delete(
      `${API}/admin/wall/signatures/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setItems(prev => prev.filter(x => x.id !== id));
  };


  return (
    <div className="grid md:grid-cols-2 gap-4">

      {items.map(i => (
        <div
          key={i.id}
          className="bg-white border border-white/[0.06] p-4 rounded-xl"
        >

          <img
            src={i.assetUrl}
            alt=""
            className="w-full rounded mb-3"
          />

          <div className="flex gap-2">

            <button
              onClick={() => approve(i.id)}
              className="bg-green-500 text-black px-4 py-1 text-xs rounded"
            >
              Onayla
            </button>

            <button
              onClick={() => remove(i.id)}
              className="bg-red-500 text-black px-4 py-1 text-xs rounded"
            >
              Sil
            </button>

          </div>

        </div>
      ))}

    </div>
  );
}
function SignatureManagerApproved({ token }) {

  const [items, setItems] = useState([]);

  useEffect(() => {

    const fetchItems = async () => {
      const res = await axios.get(
        `${API}/admin/wall/signatures?status=approved`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setItems(res.data);
    };

    fetchItems();

  }, [token]);


  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;

    await axios.delete(
      `${API}/admin/wall/signatures/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setItems(prev => prev.filter(x => x.id !== id));
  };


  return (
    <div className="grid md:grid-cols-2 gap-4">

      {items.map(i => (
        <div
          key={i.id}
          className="bg-white border border-white/[0.06] p-4 rounded-xl"
        >

          <img
            src={i.assetUrl}
            alt=""
            className="w-full rounded mb-3"
          />

          <div className="flex gap-2">

            <button
              onClick={() => remove(i.id)}
              className="bg-red-500 text-black px-4 py-1 text-xs rounded"
            >
              Sil
            </button>

          </div>

        </div>
      ))}

    </div>
  );
}


// ================= MAIN =================

export default function Admin() {

  const [token, setToken] = useState(() =>
    localStorage.getItem('zateviss-admin-token')
  );

  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(false);


  const fetchData = useCallback(async () => {

    if (!token) return;

    setLoading(true);

    try {

      const headers = { Authorization: `Bearer ${token}` };

      const [b] = await Promise.all([
        axios.get(`${API}/admin/briefs`, { headers }),
      ]);

      setBriefs(b.data);

    } catch {
      handleLogout();
    } finally {
      setLoading(false);
    }

  }, [token]);


  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const handleLogin = (t) => {
    localStorage.setItem('zateviss-admin-token', t);
    setToken(t);
  };

  const handleLogout = () => {
    localStorage.removeItem('zateviss-admin-token');
    setToken(null);
  };


  if (!token) return <LoginForm onLogin={handleLogin} />;


  return (
    <main className="pt-28 pb-24 min-h-screen">

      <div className="max-w-5xl mx-auto px-6">

        <div className="flex justify-between mb-10">
          <h1 className="text-3xl font-bold">Admin Panel</h1>

          <button onClick={handleLogout} className="flex gap-2">
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>


        <Tabs defaultValue="briefs">

          <TabsList className="mb-8">

            <TabsTrigger value="briefs">
              <FileText className="w-4 h-4 mr-2" />
              Briefs ({briefs.length})
            </TabsTrigger>

            <TabsTrigger value="invites">
              <LinkIcon className="w-4 h-4 mr-2" />
              Invites
            </TabsTrigger>

            <TabsTrigger value="signatures">
              <PenBox className="w-4 h-4 mr-2" />
              Onay Bekleyen İmzalar
            </TabsTrigger>
            <TabsTrigger value="signatures-approved">
              <PenTool className="w-4 h-4 mr-2" />
              Mevcut İmzalar
            </TabsTrigger>

          </TabsList>


          <TabsContent value="briefs">
            {briefs.map(i => (
              <SubmissionCard
                key={i.id}
                item={i}
                token={token}
                onDelete={(id) =>
                  setBriefs(prev => prev.filter(x => x.id !== id))
                }
              />
            ))}
          </TabsContent>


          <TabsContent value="invites">
            <InviteManager token={token} />
          </TabsContent>


          <TabsContent value="signatures">
            <SignatureManager token={token} />
          </TabsContent>
          <TabsContent value="signatures-approved">
            <SignatureManagerApproved token={token} />
          </TabsContent>

        </Tabs>

      </div>
    </main>
  );
}
